import { useEffect, useMemo, useRef, useState } from 'react';
import type { Metro } from '../../types/metro';
import { ChevronDown } from '../shared/icons/ChevronDown';

interface MetroSearchComboboxProps {
  readonly metros: ReadonlyArray<Metro>;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * Custom searchable dropdown for the map sidebar. Replaces the native
 * `<datalist>` combobox so the dropdown menu can be styled with the
 * platform's brand tokens (`bg-ice` hover, `text-navy` active,
 * `border-border` chrome) — datalist's browser-rendered dropdown is
 * not stylable and visually clashed with the rest of the controls.
 *
 * Behavior:
 *   - Opens on focus or click; closes on outside-click or Escape.
 *   - Filters options as the user types (case-insensitive substring).
 *   - Click an option → fills input with the full metro name + closes.
 *   - Keyboard: ArrowDown / ArrowUp navigate highlighted item,
 *     Enter commits the highlight, Escape closes.
 *
 * State source: `value` and `onChange` are wired to the cross-page
 * `useFilterStore.search`, so the typed text continues to drive the
 * map + list filter the same way the prior `<SearchInput>` did.
 */
export function MetroSearchCombobox({ metros, value, onChange }: MetroSearchComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return metros;
    return metros.filter((m) => m.name.toLowerCase().includes(q));
  }, [metros, value]);

  // Outside-click / Escape close
  useEffect(() => {
    if (!isOpen) return;
    function onDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Reset highlight when the filtered list changes.
  useEffect(() => {
    setHighlight(0);
  }, [value]);

  function commit(name: string) {
    onChange(name);
    setIsOpen(false);
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && filtered[highlight]) {
        e.preventDefault();
        commit(filtered[highlight]!.name);
      }
    }
  }

  return (
    <div className="mx-5 mt-[14px] flex-shrink-0" ref={containerRef}>
      <label className="block">
        <span className="sr-only">Search metro areas</span>
        <div className="relative">
          {/* Magnifier — left edge */}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKey}
            placeholder="Search metro areas…"
            aria-label="Search metro areas"
            autoComplete="off"
            // APG combobox pattern (WAI-ARIA 1.2) — input announces it
            // controls a listbox, exposes its open state, and points at
            // the currently-highlighted option via aria-activedescendant
            // so screen readers track keyboard navigation inside the
            // listbox without focus actually leaving the input.
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="metro-search-listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              isOpen && filtered[highlight]
                ? `metro-option-${filtered[highlight]!.id}`
                : undefined
            }
            className={`block w-full rounded-md border border-border bg-white py-2 pl-9 text-sm text-text-1 placeholder:text-text-4 transition hover:border-text-4 focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20 ${
              value ? 'pr-14' : 'pr-9'
            }`}
          />
          {/* Clear (X) — appears only once there's a query, so the user
              can drop the search with one click instead of manually
              deleting the text (CR-08 / Maria 3c, 2026-07-08: matches
              the existing pattern on MetroAreaCombobox /
              OccupationGroupCombobox, which already had this affordance
              -- the map's metro search was the one combobox missing it).
              `mousedown` (not `click`) so the pick fires before the
              input's blur kills `isOpen`. */}
          {value && (
            <button
              type="button"
              aria-label="Clear metro search"
              title="Clear metro search"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange('');
                setIsOpen(false);
                inputRef.current?.focus();
              }}
              // WCAG 2.2 SC 2.5.8 Target Size (Minimum): 24x24px hit target
              // (h-6 w-6; was h-5 w-5 = 20px). Mirrors MetroAreaCombobox.
              className="absolute right-8 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-text-4 transition hover:bg-ice hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              >
                <path d="M1 1L9 9M9 1L1 9" />
              </svg>
            </button>
          )}
          <ChevronDown
            aria-hidden="true"
            className={`pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-4 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </label>

      {isOpen && (
        <ul
          id="metro-search-listbox"
          role="listbox"
          aria-label="Metro areas"
          className="absolute z-[450] mt-1 max-h-[260px] w-[calc(288px-40px)] overflow-y-auto overscroll-contain rounded-md border border-border bg-white shadow-panel"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm italic text-text-4">No matches</li>
          ) : (
            filtered.map((m, i) => {
              const isHi = i === highlight;
              return (
                <li
                  key={m.id}
                  id={`metro-option-${m.id}`}
                  role="option"
                  aria-selected={isHi}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => {
                    // mousedown (not click) so it fires before the
                    // input's blur kills `isOpen` — otherwise the
                    // listbox unmounts before the click registers.
                    e.preventDefault();
                    commit(m.name);
                  }}
                  className={`cursor-pointer px-3 py-2 text-sm transition ${
                    isHi ? 'bg-ice text-navy' : 'text-text-1 hover:bg-ice hover:text-navy'
                  }`}
                >
                  {m.name}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
